import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import { Colors } from "../../../../styles/Colors";
import { GlobalConsts } from "../../../../styles/GlobalConsts";
import { $getRoot, $createParagraphNode, $getNodeByKey } from "lexical";
import { $getTableNodeFromLexicalNodeOrThrow, $createTableCellNode, TableCellHeaderStates, $createTableRowNode } from "@lexical/table";
import { createPortal } from "react-dom"
import "../styles/Table.scss"
import CustomButtonV2 from "../../CustomButtonV2";
import AppText from "../../AppText";
import { isRTL, t } from "../../../../services/i18n";
import { Dropdown } from "antd";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { light } from "@fortawesome/fontawesome-svg-core/import.macro";


// this is ALL cursor but mostly based on the king who did this: https://codesandbox.io/s/lexical-table-plugin-example-zd6k44
const TableCellActionMenuContainer = (props) => {
    const { anchorElem } = props;
    const [openDropdownKey, setOpenDropdownKey] = useState(null);
    const [editor] = useLexicalComposerContext();

    const menuItems = useMemo(() => {
        const items = [
            { key: 'insert-row-above' },
            { key: 'insert-row-below' },
            { type: 'divider' },
            { key: 'insert-column-left' },
            { key: 'insert-column-right' },
            { type: 'divider' },
            { key: 'delete-column' },
            { key: 'delete-row' },
            { key: 'delete-table' },
        ]
        return items.map(item => item.type === 'divider' ? item : ({ ...item, label: <AppText>{t(item.key)}</AppText> }))
    }, []);

    const [tableCells, setTableCells] = useState([]);
    const cellRefs = useRef(new Map());
    const [cellPositions, setCellPositions] = useState({});

    useEffect(() => {
        return editor.registerUpdateListener(() => {
            editor.getEditorState().read(() => {
                updateTableCells()
            });
        });
    }, [editor]);

    useEffect(() => {
        updateAllCellPositions();
    }, [tableCells]);

    useEffect(() => {
        const handlePositionUpdate = () => {
            updateAllCellPositions();
        };
        window.addEventListener('scroll', handlePositionUpdate, true); // true = capture phase for nested scrolls
        window.addEventListener('resize', handlePositionUpdate);

        // Also listen to scroll on the anchor element itself
        anchorElem?.addEventListener('scroll', handlePositionUpdate);
        return () => {
            window.removeEventListener('scroll', handlePositionUpdate, true);
            window.removeEventListener('resize', handlePositionUpdate);
            anchorElem?.removeEventListener('scroll', handlePositionUpdate);
        };
    }, [tableCells, anchorElem]); // Re-attach when cells or anchor change

    const updateTableCells = () => {
        const root = $getRoot();
        const cellKeys = [];

        const findTableCells = (node) => {
            if (node.getType && node.getType() === 'tablecell') {
                cellKeys.push(node.getKey());
            }
            if (node.getChildren) {
                node.getChildren().forEach(findTableCells);
            }
        };

        root.getChildren().forEach(findTableCells);
        setTableCells(cellKeys);
    }

    const updateAllCellPositions = () => {
        const newPositions = {};
        tableCells.forEach(cellKey => {
            const buttonDOM = cellRefs.current.get(cellKey);
            const cellDOM = editor.getElementByKey(cellKey);

            if (buttonDOM && cellDOM) {
                const cellRect = cellDOM.getBoundingClientRect();
                const anchorRect = anchorElem.getBoundingClientRect();
                const menuRect = buttonDOM.getBoundingClientRect();
                const computedStyle = window.getComputedStyle(cellDOM);
                const isContentRTL = computedStyle.direction === 'rtl';

                let left;
                if (isContentRTL) {
                    left = `${cellRect.left - anchorRect.left + 4}px`
                } else {
                    left = `${cellRect.right - menuRect.width - 10 - anchorRect.left + 4}px`
                }
                newPositions[cellKey] = {
                    opacity: 1,
                    left: left,
                    top: `${cellRect.top - anchorRect.top + 4}px`
                };
            }
        });
        setCellPositions(newPositions);
    }

    const menuOptionSelected = ({ key: actionKey }, cellKey) => {
        editor.update(() => {
            const cellNode = $getNodeByKey(cellKey);
            if (!cellNode) return; // Node no longer exists
            // Detect RTL from the DOM element
            const cellDOM = editor.getElementByKey(cellKey);
            const isContentRTL = cellDOM ? window.getComputedStyle(cellDOM).direction === 'rtl' : false;

            switch (actionKey) {
                case 'insert-row-above':
                    insertRow(cellNode, 'above')
                    break;
                case 'insert-row-below':
                    insertRow(cellNode, 'below')
                    break;
                case 'insert-column-left':
                    insertColumn(cellNode, isContentRTL ? 'right' : 'left')
                    break;
                case 'insert-column-right':
                    insertColumn(cellNode, isContentRTL ? 'left' : 'right')
                    break;
                case 'delete-column':
                    deleteColumn(cellNode)
                    break;
                case 'delete-row':
                    deleteRow(cellNode)
                    break;
                case 'delete-table':
                    deleteTable(cellNode)
                    break;
                default:
                    break;
            }
        });
    }

    const insertRow = (cellNode, position) => {
        const rowNode = cellNode.getParent(); // Get current row
        if (!rowNode) return;
        const existingCells = rowNode.getChildren(); // Number of cells in row

        // Create new row
        const newRow = $createTableRowNode();

        // Add cells to the new row, matching header states from current row
        existingCells.forEach((existingCell, columnIndex) => {
            // Check if this column is a header column (first column typically)
            const headerState = existingCell.getHeaderStyles();
            const isColumnHeader = headerState === TableCellHeaderStates.COLUMN ||
                headerState === TableCellHeaderStates.BOTH;

            const newCell = $createTableCellNode(
                isColumnHeader ? TableCellHeaderStates.COLUMN : TableCellHeaderStates.NO_STATUS
            );
            newCell.append($createParagraphNode());
            newRow.append(newCell);
        });

        // Insert the new row above or below
        if (position === 'above') {
            rowNode.insertBefore(newRow);
        } else {
            rowNode.insertAfter(newRow);
        }
    }

    const insertColumn = (cellNode, position) => {
        const tableNode = $getTableNodeFromLexicalNodeOrThrow(cellNode);
        const columnIndex = cellNode.getIndexWithinParent();
        const rows = tableNode.getChildren();

        rows.forEach((row, rowIndex) => {
            const cells = row.getChildren();
            const targetCell = cells[columnIndex];

            // Check if this row is a header row
            const isHeaderRow = rowIndex === 0 && targetCell?.getHeaderStyles?.() !== TableCellHeaderStates.NO_STATUS;

            // Create new cell
            const newCell = $createTableCellNode(
                isHeaderRow ? TableCellHeaderStates.ROW : TableCellHeaderStates.NO_STATUS
            );
            newCell.append($createParagraphNode());

            // Insert after the target cell
            if (targetCell) {
                if (position === 'right') targetCell.insertAfter(newCell);
                else targetCell.insertBefore(newCell);
            }
        });
    }

    const deleteColumn = (cellNode) => {
        const tableNode = $getTableNodeFromLexicalNodeOrThrow(cellNode);
        const columnIndex = cellNode.getIndexWithinParent(); // Get column position
        const rows = tableNode.getChildren(); // Get all rows

        // Check if this is the only column
        const firstRow = rows[0];
        const columnCount = firstRow ? firstRow.getChildrenSize() : 0;

        if (columnCount <= 1) {
            // If only one column, delete the entire table
            tableNode.remove();
        } else {
            // Remove the cell at this column index from each row
            rows.forEach(row => {
                const cells = row.getChildren();
                const cellToRemove = cells[columnIndex];
                if (cellToRemove) {
                    cellToRemove.remove();
                }
            });
        }
    }

    const deleteRow = (cellNode) => {
        const tableNode = $getTableNodeFromLexicalNodeOrThrow(cellNode);
        const rowNode = cellNode.getParent(); // The cell's parent is the TableRowNode

        if (rowNode) {
            const rows = tableNode.getChildren();
            // If this is the last row, delete the entire table instead
            if (rows.length === 1) {
                tableNode.remove();
            } else {
                rowNode.remove();
            }
        }
    }

    const deleteTable = (cellNode) => {
        const tableNode = $getTableNodeFromLexicalNodeOrThrow(cellNode);
        tableNode.remove();
    }

    return (
        <div className="table-cell-action-button-container">
            {tableCells.map((key, i) => {
                const pos = cellPositions[key] || { opacity: 0, left: 0, top: 0 };
                const isDropdownOpen = openDropdownKey === key;
                return (
                    <div key={key} style={{ position: 'absolute', ...pos }}
                        ref={(el) => {
                            if (el) cellRefs.current.set(key, el);
                            else cellRefs.current.delete(key);
                        }}>
                        <Dropdown menu={{ items: menuItems, onClick: (info) => menuOptionSelected(info, key) }} trigger={['click']} onOpenChange={(open) => setOpenDropdownKey(open ? key : null)}>
                            <CustomButtonV2 override={({ isHovered }) => (<div className={`table-cell-btn ${isHovered || isDropdownOpen ? 'active-menu' : ''}`}><FontAwesomeIcon icon={light('chevron-down')} fontSize={GlobalConsts.iconSize.XXS10} color={isHovered || isDropdownOpen ? Colors.newDesign.mainText : 'transparent'} /></div>)} />
                        </Dropdown>
                    </div>
                )
            })}
        </div>
    )
}

const TableCellActionPlugin = (props) => {
    const { anchor } = props;
    return createPortal(<TableCellActionMenuContainer anchorElem={anchor} />, anchor);
};

export default TableCellActionPlugin;